import React from 'react';
import { IconButton } from '@eventbrite/legacy-eds.icon-button';
import { injectIntl, type MessageDescriptor, type WrappedComponentProps } from 'react-intl';
import { MailFillChunky } from '@eventbrite/legacy-eds.iconography';
import { EMAIL_AFFILIATED_CODE } from './constants';
import { openUrl } from './utils';
import type { UtmOptions } from './types';

interface EmailShareProps {
    className?: string;
    resourceId: string;
    resourceName: string;
    resourceUrl: string;
    resourceType: string;
    serverUrl: string;
    isMobile?: boolean;
    onClick?: () => void;
    utmOptions: UtmOptions;
    trackingCode?: string;
    emailOptions?: {
        bodyCopy?: MessageDescriptor;
        subjectCopy?: MessageDescriptor;
    };

    affiliateChannel?: string;
    dataHeapId?: string;
    shareMethod?: string;
    dataPageArea?: string;
}

class EmailShareComponent extends React.Component<EmailShareProps & WrappedComponentProps<'intl'>> {
    _getMailToUrl(
        resourceName: string,
        resourceUrl: string,
        affiliateChannel = 'default',
        utmOptions: UtmOptions,
        emailOptions: {
            subject?: string;
            body?: string;
        } = {},
    ) {
        const { formatMessage } = this.props.intl;
        const defaultSubject = formatMessage(
            {
                id: 'Check out {resourceName}',
                defaultMessage: 'Check out {resourceName}',
                description:
                    'The subject an email to share an event, example: "Check out Awesome Event"',
            },
            {
                resourceName,
            },
        );

        const url = new URL(resourceUrl);
        url.searchParams.set(
            'aff',
            EMAIL_AFFILIATED_CODE[affiliateChannel] || EMAIL_AFFILIATED_CODE['default'],
        );
        Object.entries(utmOptions).forEach(([key, value]) => {
            url.searchParams.set(key, value);
        });
        const defaultBody = url.toString();

        const subject = emailOptions.subject || defaultSubject;
        const body = emailOptions.body || defaultBody;

        const urlTo = new URL('mailto:');
        urlTo.searchParams.set('subject', subject);
        urlTo.searchParams.set('body', body);
        return urlTo.toString();
    }
    _handleButtonClick() {
        const {
            affiliateChannel,
            emailOptions,
            intl,
            resourceName,
            resourceUrl,
            onClick,
            utmOptions,
        } = this.props;
        const mappedEmailOptions = emailOptions
            ? {
                  subject: emailOptions.subjectCopy
                      ? intl.formatMessage(emailOptions.subjectCopy)
                      : undefined,
                  body: emailOptions.bodyCopy
                      ? intl.formatMessage(emailOptions.bodyCopy)
                      : undefined,
              }
            : undefined;
        const url = this._getMailToUrl(
            resourceName,
            resourceUrl,
            affiliateChannel,
            utmOptions,
            mappedEmailOptions,
        );

        if (onClick) {
            onClick();
        }

        openUrl(url);
    }

    render() {
        const { formatMessage } = this.props.intl;
        return (
            <span
                className={this.props.className}
                data-testid="email-share-box-container"
                data-heap-id={this.props.dataHeapId}
                data-share-method={this.props.shareMethod}
                data-page-area={this.props.dataPageArea}
                {...{
                    [`data-${this.props.resourceType}-id`]: this.props.resourceId,
                }}
            >
                <IconButton
                    iconType={<MailFillChunky />}
                    title={formatMessage({
                        id: 'Share by Email',
                        defaultMessage: 'Share by Email',
                    })}
                    onClick={this._handleButtonClick.bind(this)}
                />
            </span>
        );
    }
}

export const EmailShare = injectIntl(EmailShareComponent);
